import { useState, useEffect } from "react";
import axios from "axios";
import HouseholdCheckinPrompt from "@/components/HouseholdCheckinPrompt";
import { RolePicker } from "@/components/checkin/RolePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });
const box = "w-full bg-white border border-ink/20 rounded-xl px-4 py-3 min-h-[52px] text-[17px]";
const bigBtn = "w-full rounded-xl px-5 py-4 min-h-[64px] text-[18px] font-semibold";

export default function PublicNameCheckIn() {
  const [ctx, setCtx] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [visitor, setVisitor] = useState(null); // null | {name, organization, phone, email}
  const [vErr, setVErr] = useState("");
  const [household, setHousehold] = useState(null);
  const [already, setAlready] = useState(false);

  useEffect(() => {
    const load = () => pub.get("/checkin/station").then((r) => setCtx(r.data)).catch(() => setCtx({ active: false }));
    load(); const t = setInterval(load, 3000); return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(() => pub.get("/checkin/members", { params: { q } }).then((r) => setResults(r.data.members)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);

  const [roles, setRoles] = useState(null);
  const handleResult = (d) => {
    const hh = d.household || [];
    if (hh.length && !d.already) setHousehold({ memberId: d.member_id, list: hh, name: d.name });
    else { setAlready(!!d.already); setDone(d.name); }
  };
  const tapKey = (d) => { setErr(""); if (d === "del") setPin((p) => p.slice(0, -1)); else if (pin.length < 4) setPin((p) => p + d); };
  const submit = async (fullPin) => {
    setErr("");
    try {
      const r = await pub.post("/checkin/member-by-id", { member_id: picked.member_id, pin: fullPin });
      if (r.data.needs_roles) { setRoles(r.data); return; }
      handleResult(r.data);
    }
    catch (e) { setErr(e.response?.data?.detail || "That didn't work."); setPin(""); }
  };
  useEffect(() => { if (pin.length === 4 && picked) submit(pin); /* eslint-disable-next-line */ }, [pin]);
  const reset = () => { setRoles(null); setPicked(null); setPin(""); setQ(""); setResults([]); setErr(""); setDone(null); setVisitor(null); setVErr(""); setHousehold(null); setAlready(false); };
  // Auto-return to the start after a successful check-in so the kiosk is ready
  // for the next person (prevents being "stuck" on the last person's success).
  useEffect(() => { if (done) { const t = setTimeout(reset, 6000); return () => clearTimeout(t); } }, [done]);

  const startVisitor = () => setVisitor({ name: q.trim(), organization: "", phone: "", email: "" });
  const submitVisitor = async () => {
    setVErr("");
    if (!visitor.name.trim()) { setVErr("Please enter a name."); return; }
    try { const r = await pub.post("/checkin/visitor/new", visitor); setAlready(!!r.data.already); setDone(r.data.name); }
    catch (e) { setVErr(e.response?.data?.detail || "That didn't work."); }
  };

  if (roles) {
    return (
      <div className="min-h-screen bg-warm-cream flex flex-col items-center px-5 py-8" data-testid="public-name-checkin">
        <div className="w-full max-w-sm"><RolePicker data={roles} onDone={(d) => { setRoles(null); handleResult(d); }} onCancel={reset} /></div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-warm-cream flex flex-col items-center px-5 py-8" data-testid="public-name-checkin">
      <img src="/logo.png" alt="Rosemoor" className="w-16 h-16 mb-3" />
      <h1 className="font-zilla text-3xl font-semibold text-center">Find your name</h1>
      {ctx?.active && ctx.event_title && <p className="text-dark-olive font-semibold text-[16px] mt-1">{ctx.event_title}</p>}
      <p className="text-ink/60 text-[15px] mb-6">Tap your name, then enter your PIN</p>

      <div className="w-full max-w-sm">
        {ctx && !ctx.active ? (
          <div className="text-center py-12" data-testid="name-checkin-closed">
            <div className="w-16 h-16 rounded-full bg-gold/25 text-ink flex items-center justify-center mx-auto mb-4 text-3xl">⏳</div>
            <h2 className="font-zilla text-2xl font-semibold mb-2">Check-in isn't open yet</h2>
            <p className="text-ink/65 text-[16px]">Please ask a volunteer to start check-in, then try again.</p>
          </div>
        ) : done ? (
          <div className="text-center py-10" data-testid="name-checkin-success">
            <div className="w-16 h-16 rounded-full bg-olive/25 text-dark-olive flex items-center justify-center mx-auto mb-4 text-3xl">✓</div>
            <h2 className="font-zilla text-3xl font-semibold mb-1">{already ? "You're already checked in" : "You're checked in!"}</h2>
            <p className="text-ink/70 text-[17px] mb-8">{already ? `Thank you, ${done}.` : `Welcome, ${done}.`}</p>
            <button data-testid="name-checkin-done" onClick={reset} className="border border-ink/25 rounded-xl px-6 py-3 min-h-[52px] text-[16px] font-semibold">Check someone else in</button>
          </div>
        ) : household ? (
          <div data-testid="name-household">
            <div className="text-center mb-2"><h2 className="font-zilla text-2xl font-semibold">{household.name} is checked in ✓</h2></div>
            <HouseholdCheckinPrompt memberId={household.memberId} household={household.list} onDone={() => { setHousehold(null); setDone(household.name); }} />
          </div>
        ) : visitor ? (
          <div data-testid="name-visitor-form">
            <button data-testid="visitor-back" onClick={() => { setVisitor(null); setVErr(""); }} className="text-dark-olive font-semibold text-[15px] mb-3">← Back</button>
            <div className="text-center mb-3"><div className="font-zilla text-2xl font-semibold">Check in as a visitor</div><div className="text-ink/55 text-[14px]">No membership needed</div></div>
            <div className="space-y-3">
              <input data-testid="name-visitor-name" value={visitor.name} onChange={(e) => setVisitor({ ...visitor, name: e.target.value })} placeholder="Your name" className={box} />
              <input data-testid="name-visitor-org" value={visitor.organization} onChange={(e) => setVisitor({ ...visitor, organization: e.target.value })} placeholder="Organization / business (optional)" className={box} />
              <input data-testid="name-visitor-phone" value={visitor.phone} onChange={(e) => setVisitor({ ...visitor, phone: e.target.value })} placeholder="Phone (optional)" className={box} />
              <input data-testid="name-visitor-email" value={visitor.email} onChange={(e) => setVisitor({ ...visitor, email: e.target.value })} placeholder="Email (optional)" className={box} />
              {vErr && <div className="text-maroon text-center text-[15px]" data-testid="name-visitor-error">{vErr}</div>}
              <button data-testid="name-visitor-submit" onClick={submitVisitor} className={`${bigBtn} bg-dark-olive text-cream`}>Check in</button>
            </div>
          </div>
        ) : !picked ? (
          <div>
            <input data-testid="name-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing your name…" className="w-full bg-white border border-ink/20 rounded-xl px-4 py-3 min-h-[52px] text-[17px] mb-3" />
            <div className="space-y-2">
              {results.map((m) => (
                <button key={m.member_id} data-testid={`name-option-${m.member_id}`} onClick={() => setPicked(m)} className="w-full text-left bg-cream border border-ink/20 rounded-xl px-4 py-3 min-h-[56px] flex items-center justify-between">
                  <span className="font-semibold text-[17px]">{m.name}</span>
                  <span className="text-[13px] text-ink/45">{m.member_number}</span>
                </button>
              ))}
              {q.trim() && results.length === 0 && (
                <div className="text-center py-6">
                  <div className="text-ink/55 mb-3">No match for “{q.trim()}”.</div>
                  <button data-testid="name-checkin-as-visitor" onClick={startVisitor} className={`${bigBtn} bg-dark-olive text-cream`}>I'm a visitor — check in</button>
                </div>
              )}
            </div>
            <button data-testid="name-visitor-cta" onClick={startVisitor} className="block w-full text-center text-dark-olive font-semibold text-[15px] pt-4">Not a member? Check in as a visitor →</button>
          </div>
        ) : (
          <div>
            <button data-testid="name-back" onClick={() => { setPicked(null); setPin(""); }} className="text-dark-olive font-semibold text-[15px] mb-3">← Back</button>
            <div className="text-center mb-3"><div className="font-zilla text-2xl font-semibold">{picked.name}</div><div className="text-ink/55 text-[14px]">Enter your 4-digit PIN</div></div>
            <div className="flex justify-center gap-3 mb-4" data-testid="pin-dots">
              {[0, 1, 2, 3].map((i) => <div key={i} className={`w-4 h-4 rounded-full ${i < pin.length ? "bg-ink" : "border-2 border-ink/30"}`} />)}
            </div>
            {err && <div className="text-maroon text-center text-[15px] mb-3" data-testid="name-pin-error">{err}</div>}
            <div className="grid grid-cols-3 gap-3">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                <button key={d} data-testid={`key-${d}`} onClick={() => tapKey(d)} className="bg-cream border border-ink/20 rounded-xl min-h-[64px] text-[24px] font-semibold">{d}</button>
              ))}
              <div />
              <button data-testid="key-0" onClick={() => tapKey("0")} className="bg-cream border border-ink/20 rounded-xl min-h-[64px] text-[24px] font-semibold">0</button>
              <button data-testid="key-del" onClick={() => tapKey("del")} className="bg-cream border border-ink/20 rounded-xl min-h-[64px] text-[18px] font-semibold">⌫</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
